import React, { useState, useMemo, useEffect } from "react";
import useSWR from "swr";
import SkeletonTable from "../../../Common/SkeletonTable";
import ErrorCard from "../../../Common/ErrorCard";
import EmptyCard from "../../../Common/EmptyCard";
import PaginationBar from "../../../Common/PaginationBar";
import { inventoryFetcher } from "../../../../network/InventoryService";
import { getInventoryTypeTitle } from "../../../../../../utilities/helpers";
import { format } from "date-fns";
import { DEFAULT_PAGE_SIZE } from "../../../../utils/inventoryHelper";

const POTable = ({ searchText, setShowPOCreateModal, onPOSelect }) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      page_index: page - 1,
      page_size: DEFAULT_PAGE_SIZE,
    });

    if (searchText) {
      params.append("search_term", searchText);
    }

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-po?${params.toString()}`;
  }, [page, searchText]);

  const {
    data: response,
    error,
    mutate,
  } = useSWR(apiUrl, inventoryFetcher, {
    fallbackData: {},
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 3,
    errorRetryInterval: 1000,
    refreshInterval: 5 * 60 * 1000,
  });

  const poData = response?.data || [];
  const totalCount = response?.count || 0;
  const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
  const loading = !response && !error;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <SkeletonTable
          columnCount={8}
          rowCount={7}
          headers={[
            "#",
            "Pay Order Number",
            "Supplier",
            "Inventory Type",
            "Total Allocation",
            "Received",
            "Total PO Value",
            "Creation Date",
            "Action",
          ]}
        />
      );
    }

    if (error) {
      return (
        <ErrorCard
          onRetryClick={mutate}
          title="Error loading POs"
          message="Failed to load PO data. Please try again."
        />
      );
    }

    if (poData.length === 0) {
      return (
        <EmptyCard
          title="No POs found"
          message={
            searchText
              ? "No POs found matching your search criteria."
              : "No PO data available. Create your first purchase order."
          }
        />
      );
    }

    return (
      <table className="inventory-table po-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Pay Order Number</th>
            <th>Supplier</th>
            <th>Inventory Type</th>
            <th>Total Allocation</th>
            <th>Received</th>
            <th>Total PO Value</th>
            <th>Creation Date</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {poData.map((po, index) => (
            <tr key={po.id || index} className="table-row">
              <td>{(page - 1) * DEFAULT_PAGE_SIZE + index + 1}</td>
              <td className="po-number">{po.po_number || ""}</td>
              <td title={po.supplier__name || ""}>
                {po.supplier__name || "-"}
              </td>
              <td>{getInventoryTypeTitle(po.model_type)}</td>
              <td>
                {po.total_allocation
                  ? formatCurrency(po.total_allocation)
                  : "-"}
              </td>
              <td>
                {po.total_received ? formatCurrency(po.total_received) : "-"}
              </td>
              <td>{po.total_cost ? formatCurrency(po.total_cost) : ""}</td>
              <td>
                {po.created ? format(new Date(po.created), "d LLLL yyyy") : "-"}
              </td>
              <td>
                <button
                  className="details-btn"
                  onClick={() => {
                    const poDetails = {
                      attachment: po.attachment || null,
                      poNumber: po.po_number || "",
                      poInitiator: po.po_initiator || "",
                      inventoryType: po.model_type || "",
                      description: po.description || "",
                      supplier: po.supplier__name || "",
                      devices: po.device_info
                        ? po.device_info.map((device) => ({
                            item: device.model_id || "",
                            itemName: device.model_name || "",
                            quantity: device.quantity
                              ? device.quantity.toString()
                              : "",
                            unitCost: device.unit_cost
                              ? device.unit_cost.toString()
                              : "",
                            totalCost: device.total
                              ? device.total.toLocaleString()
                              : "",
                          }))
                        : [],
                    };
                    onPOSelect(poDetails);
                  }}
                >
                  Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <>
      <div className="inventory-table-container">{renderTableContent()}</div>

      {totalCount > 0 && !loading && !error && (
        <PaginationBar
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalCount={totalCount}
        />
      )}
    </>
  );
};

export default POTable;
